import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api } from "../api";
import { PanelDomainSslCard } from "../components/PanelDomainSslCard";

type Dual = {
  mode: "recommended" | "custom";
  recommended: number;
  custom: number;
};

type PanelConfig = {
  hostname: string;
  primaryIp: string;
  primaryIpv6: string;
  enableQuota: boolean;
  ns1: string;
  ns2: string;
  ns3: string;
  ns4: string;
  adminEmail: string;
  fromEmail: string;
  systemHostname?: string;
};

type PanelPhp = {
  maxExecutionTime: Dual;
  maxPostSizeMb: Dual;
  maxUploadSizeMb: Dual;
  maxInputVars: Dual;
  clientMaxBodySizeMb: Dual;
  fpmMaxChildren: Dual;
  fpmStartServers: Dual;
  fpmMinSpareServers: Dual;
  fpmMaxSpareServers: Dual;
  fpmMaxRequests: Dual;
  fpmRequestTerminateTimeout: Dual;
  memoryLimitMb: Dual;
};

type PanelPorts = {
  redirectToSsl: boolean;
  redirectToHostname: boolean;
  disablePanelAlias: boolean;
  adminPortSsl: string;
  adminPortNonSsl: string;
  enduserPortSsl: string;
  enduserPortNonSsl: string;
  enduserPanelAlias: string;
  adminPanelAlias: string;
};

const PHP_ROWS: {
  key: keyof PanelPhp;
  label: string;
  unit: string;
  hint: string;
}[] = [
  {
    key: "maxExecutionTime",
    label: "PHP Max Execution Time",
    unit: "sec",
    hint: "Minimum : 30"
  },
  {
    key: "maxPostSizeMb",
    label: "PHP Max POST Size",
    unit: "MB",
    hint: "Minimum : 128 · Maximum : 16384"
  },
  {
    key: "maxUploadSizeMb",
    label: "PHP Max Upload Size",
    unit: "MB",
    hint: "Minimum : 128 · Maximum : 16384"
  },
  {
    key: "maxInputVars",
    label: "PHP Max Input Variables",
    unit: "",
    hint: "Minimum : 1000"
  },
  {
    key: "clientMaxBodySizeMb",
    label: "Service Client Max Body Size",
    unit: "MB",
    hint: "Nginx / API body limit (panel uploads)"
  },
  {
    key: "fpmMaxChildren",
    label: "PHP-FPM Max Children",
    unit: "",
    hint: "Per-account pool max processes"
  },
  {
    key: "fpmStartServers",
    label: "PHP-FPM Start Servers",
    unit: "",
    hint: "Used when pm=dynamic"
  },
  {
    key: "fpmMinSpareServers",
    label: "PHP-FPM Min Spare Servers",
    unit: "",
    hint: "Used when pm=dynamic"
  },
  {
    key: "fpmMaxSpareServers",
    label: "PHP-FPM Max Spare Servers",
    unit: "",
    hint: "Used when pm=dynamic"
  },
  {
    key: "fpmMaxRequests",
    label: "PHP-FPM Max Request",
    unit: "",
    hint: "Recycle workers after N requests"
  },
  {
    key: "fpmRequestTerminateTimeout",
    label: "PHP-FPM Request Terminate Timeout",
    unit: "sec",
    hint: "Kill stuck PHP workers"
  },
  {
    key: "memoryLimitMb",
    label: "PHP Memory Limit",
    unit: "MB",
    hint: "Minimum : 128"
  }
];

function DualRow({
  label,
  hint,
  unit,
  value,
  onChange
}: {
  label: string;
  hint: string;
  unit: string;
  value: Dual;
  onChange: (v: Dual) => void;
}) {
  const recLabel =
    unit === "sec"
      ? `${value.recommended} seconds`
      : unit === "MB"
        ? `${value.recommended} MB`
        : String(value.recommended);

  return (
    <div
      className="panel-dual-row"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 16,
        alignItems: "center",
        padding: "12px 0",
        borderBottom: "1px solid var(--border, #e5e7eb)"
      }}
    >
      <div>
        <div style={{ fontWeight: 600, color: "var(--accent, #2563eb)" }}>
          {label}
        </div>
        <div className="card-meta faint" style={{ fontSize: 12 }}>
          {hint}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer"
          }}
        >
          <input
            type="radio"
            checked={value.mode === "recommended"}
            onChange={() => onChange({ ...value, mode: "recommended" })}
          />
          <span className="mono">{recLabel}</span>
        </label>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer"
          }}
        >
          <input
            type="radio"
            checked={value.mode === "custom"}
            onChange={() => onChange({ ...value, mode: "custom" })}
          />
          <input
            type="number"
            className="mono"
            style={{ width: 120 }}
            value={value.custom}
            disabled={value.mode !== "custom"}
            onChange={(e) =>
              onChange({
                ...value,
                mode: "custom",
                custom: Number(e.target.value)
              })
            }
          />
          {unit ? (
            <span className="faint" style={{ fontSize: 12 }}>
              {unit}
            </span>
          ) : null}
        </label>
      </div>
    </div>
  );
}

/**
 * Webuzo-style Panel Configuration — hostname/IP/NS, PHP/FPM limits, ports & aliases.
 */
export function PanelConfigPage() {
  const [config, setConfig] = useState<PanelConfig | null>(null);
  const [php, setPhp] = useState<PanelPhp | null>(null);
  const [ports, setPorts] = useState<PanelPorts | null>(null);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState("");
  const [setSystemHostname, setSetSystemHostname] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const res = await api<{
        config: PanelConfig;
        php: PanelPhp;
        ports: PanelPorts;
      }>("/api/v1/server-config/panel");
      setConfig(res.config);
      setPhp(res.php);
      setPorts(res.ports);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const saveConfig = async (e: FormEvent) => {
    e.preventDefault();
    if (!config) return;
    setBusy("config");
    setError("");
    setMsg("");
    try {
      const res = await api<{ config: PanelConfig }>(
        "/api/v1/server-config/panel",
        {
          method: "PUT",
          body: JSON.stringify(config)
        }
      );
      setConfig(res.config);
      setMsg("Panel configuration saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const savePhp = async (e: FormEvent) => {
    e.preventDefault();
    if (!php) return;
    setBusy("php");
    setError("");
    setMsg("");
    try {
      const res = await api<{ php: PanelPhp }>(
        "/api/v1/server-config/panel/php",
        {
          method: "PUT",
          body: JSON.stringify(php)
        }
      );
      setPhp(res.php);
      setMsg("Panel PHP / FPM settings saved — click Reconfigure to apply on server");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const savePorts = async (e: FormEvent) => {
    e.preventDefault();
    if (!ports) return;
    setBusy("ports");
    setError("");
    setMsg("");
    try {
      const res = await api<{ ports: PanelPorts }>(
        "/api/v1/server-config/panel/ports",
        {
          method: "PUT",
          body: JSON.stringify(ports)
        }
      );
      setPorts(res.ports);
      setMsg("Panel ports & aliases saved");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  const reconfigure = async () => {
    if (!config || !php || !ports) return;
    setBusy("reconfigure");
    setError("");
    setMsg("");
    try {
      await api("/api/v1/server-config/panel/reconfigure", {
        method: "POST",
        body: JSON.stringify({
          config,
          php,
          ports,
          setSystemHostname,
          applyPhp: true
        })
      });
      setMsg(
        "Reconfigure complete — PHP/FPM defaults, nginx body size, and panel maps applied"
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy("");
    }
  };

  if (!config || !php || !ports) {
    return (
      <section className="card full" style={{ marginTop: 8 }}>
        <p className="card-meta">
          {error || "Loading panel configuration…"}
        </p>
      </section>
    );
  }

  return (
    <>
      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}

      {/* ── Update Panel Configuration ── */}
      <form className="panel-form" onSubmit={(e) => void saveConfig(e)}>
        <div
          className="topbar-actions"
          style={{ justifyContent: "space-between", marginBottom: 8 }}
        >
          <div className="panel-form-title" style={{ margin: 0 }}>
            Update RealPanel Configuration
          </div>
          <button
            type="button"
            className="btn btn-primary btn-inline"
            disabled={busy === "reconfigure"}
            onClick={() => void reconfigure()}
            title="Apply hostname, PHP/FPM, nginx body size on the server"
          >
            {busy === "reconfigure" ? "Reconfiguring…" : "Reconfigure"}
          </button>
        </div>
        <p className="card-meta" style={{ marginBottom: 12 }}>
          Hostname, primary IPs, nameservers, admin mail, and disk quota policy.
          {config.systemHostname
            ? ` System hostname: ${config.systemHostname}`
            : ""}{" "}
          First login is always via{" "}
          <strong className="mono">
            http://{config.primaryIp || "SERVER_IP"}:7001
          </strong>{" "}
          (admin) /{" "}
          <strong className="mono">:7002</strong> (user). After Panel Domain
          SSL, domain HTTPS and IP:port both keep working.
        </p>
        <div className="form-grid">
          <div className="field">
            <label>Hostname / Panel Domain</label>
            <input
              className="mono"
              value={config.hostname}
              onChange={(e) =>
                setConfig({ ...config, hostname: e.target.value })
              }
              placeholder="real.example.com"
            />
          </div>
          <div className="field">
            <label>Enable Quota</label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 8
              }}
            >
              <input
                type="checkbox"
                checked={config.enableQuota}
                onChange={(e) =>
                  setConfig({ ...config, enableQuota: e.target.checked })
                }
              />
              <span
                className={`badge ${config.enableQuota ? "status-active" : ""}`}
              >
                {config.enableQuota ? "ON" : "OFF"}
              </span>
            </label>
          </div>
          <div className="field">
            <label>Primary IP</label>
            <input
              className="mono"
              value={config.primaryIp}
              onChange={(e) =>
                setConfig({ ...config, primaryIp: e.target.value })
              }
              placeholder="203.0.113.10"
            />
          </div>
          <div className="field">
            <label>Primary IPv6</label>
            <input
              className="mono"
              value={config.primaryIpv6}
              onChange={(e) =>
                setConfig({ ...config, primaryIpv6: e.target.value })
              }
              placeholder="optional"
            />
          </div>
          <div className="field">
            <label>NS1</label>
            <input
              className="mono"
              value={config.ns1}
              onChange={(e) => setConfig({ ...config, ns1: e.target.value })}
              placeholder="ns1.example.com"
            />
          </div>
          <div className="field">
            <label>NS2</label>
            <input
              className="mono"
              value={config.ns2}
              onChange={(e) => setConfig({ ...config, ns2: e.target.value })}
              placeholder="ns2.example.com"
            />
          </div>
          <div className="field">
            <label>NS3</label>
            <input
              className="mono"
              value={config.ns3}
              onChange={(e) => setConfig({ ...config, ns3: e.target.value })}
            />
          </div>
          <div className="field">
            <label>NS4</label>
            <input
              className="mono"
              value={config.ns4}
              onChange={(e) => setConfig({ ...config, ns4: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Admin Email Address</label>
            <input
              type="email"
              value={config.adminEmail}
              onChange={(e) =>
                setConfig({ ...config, adminEmail: e.target.value })
              }
            />
          </div>
          <div className="field">
            <label>From Email Address</label>
            <input
              type="email"
              value={config.fromEmail}
              onChange={(e) =>
                setConfig({ ...config, fromEmail: e.target.value })
              }
            />
          </div>
        </div>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            margin: "12px 0"
          }}
        >
          <input
            type="checkbox"
            checked={setSystemHostname}
            onChange={(e) => setSetSystemHostname(e.target.checked)}
          />
          Also set OS hostname on Reconfigure (Linux root)
        </label>
        <button
          type="submit"
          className="btn btn-primary btn-inline"
          disabled={busy === "config"}
        >
          {busy === "config" ? "Saving…" : "Update"}
        </button>
      </form>

      <div style={{ marginTop: 20 }}>
        <PanelDomainSslCard isStaff={true} />
      </div>

      {/* ── Panel Settings (PHP / FPM) ── */}
      <form
        className="panel-form"
        style={{ marginTop: 20 }}
        onSubmit={(e) => void savePhp(e)}
      >
        <div className="panel-form-title">RealPanel Panel Settings</div>
        <p className="card-meta" style={{ marginBottom: 8 }}>
          Recommended (high) or custom values — same pattern as Webuzo. Applied
          on <strong>Reconfigure</strong> to PHP conf.d and FPM pool defaults.
        </p>
        {PHP_ROWS.map((row) => (
          <DualRow
            key={row.key}
            label={row.label}
            hint={row.hint}
            unit={row.unit}
            value={php[row.key]}
            onChange={(v) => setPhp({ ...php, [row.key]: v })}
          />
        ))}
        <button
          type="submit"
          className="btn btn-primary btn-inline"
          disabled={busy === "php"}
          style={{ marginTop: 16 }}
        >
          {busy === "php" ? "Saving…" : "Update"}
        </button>
      </form>

      {/* ── Panel Ports ── */}
      <form
        className="panel-form"
        style={{ marginTop: 20 }}
        onSubmit={(e) => void savePorts(e)}
      >
        <div className="panel-form-title">RealPanel Panel Ports</div>
        <p className="card-meta" style={{ marginBottom: 10 }}>
          Default admin <strong className="mono">7001</strong> · user{" "}
          <strong className="mono">7002</strong>. These ports stay open after
          domain SSL so you can always fall back to IP access. Prefer domain
          HTTPS for daily use; keep IP:port for recovery.
        </p>
        <div
          className="form-grid"
          style={{ marginBottom: 12 }}
        >
          <label className="feature-check">
            <input
              type="checkbox"
              checked={ports.redirectToSsl}
              onChange={(e) =>
                setPorts({ ...ports, redirectToSsl: e.target.checked })
              }
            />
            <span>
              <strong>Redirect domain HTTP → HTTPS</strong>
              <span className="faint" style={{ display: "block", fontSize: 12 }}>
                Does not block IP:7001 / IP:7002
              </span>
            </span>
          </label>
          <label className="feature-check">
            <input
              type="checkbox"
              checked={ports.redirectToHostname}
              onChange={(e) =>
                setPorts({ ...ports, redirectToHostname: e.target.checked })
              }
            />
            <span>
              <strong>Prefer hostname in UI links</strong>
              <span className="faint" style={{ display: "block", fontSize: 12 }}>
                Display preference only — IP login remains enabled
              </span>
            </span>
          </label>
          <label className="feature-check">
            <input
              type="checkbox"
              checked={ports.disablePanelAlias}
              onChange={(e) =>
                setPorts({ ...ports, disablePanelAlias: e.target.checked })
              }
            />
            <span>
              <strong>Disable Panel Alias</strong>
            </span>
          </label>
        </div>
        <div className="form-grid">
          <div className="field">
            <label>Custom Admin Port SSL</label>
            <input
              className="mono"
              value={ports.adminPortSsl}
              onChange={(e) =>
                setPorts({ ...ports, adminPortSsl: e.target.value })
              }
              placeholder="e.g. 2087"
            />
          </div>
          <div className="field">
            <label>Admin Port Non SSL</label>
            <input
              className="mono"
              value={ports.adminPortNonSsl}
              onChange={(e) =>
                setPorts({ ...ports, adminPortNonSsl: e.target.value })
              }
              placeholder="7480"
            />
          </div>
          <div className="field">
            <label>Enduser Port SSL</label>
            <input
              className="mono"
              value={ports.enduserPortSsl}
              onChange={(e) =>
                setPorts({ ...ports, enduserPortSsl: e.target.value })
              }
              placeholder="e.g. 2083"
            />
          </div>
          <div className="field">
            <label>Enduser Port Non SSL</label>
            <input
              className="mono"
              value={ports.enduserPortNonSsl}
              onChange={(e) =>
                setPorts({ ...ports, enduserPortNonSsl: e.target.value })
              }
              placeholder="7480"
            />
          </div>
          <div className="field">
            <label>Enduser Panel Alias</label>
            <input
              className="mono"
              value={ports.enduserPanelAlias}
              onChange={(e) =>
                setPorts({ ...ports, enduserPanelAlias: e.target.value })
              }
              placeholder="realpanel,cpanel"
            />
          </div>
          <div className="field">
            <label>Admin Panel Alias</label>
            <input
              className="mono"
              value={ports.adminPanelAlias}
              onChange={(e) =>
                setPorts({ ...ports, adminPanelAlias: e.target.value })
              }
              placeholder="whm"
            />
          </div>
        </div>
        <p className="card-meta faint" style={{ marginTop: 8 }}>
          Aliases are path/host hints for reverse proxies (e.g.{" "}
          <span className="mono">/whm</span>, <span className="mono">/cpanel</span>
          ). Changing live listen ports requires a service restart and firewall
          rules.
        </p>
        <button
          type="submit"
          className="btn btn-primary btn-inline"
          disabled={busy === "ports"}
          style={{ marginTop: 12 }}
        >
          {busy === "ports" ? "Saving…" : "Update"}
        </button>
      </form>
    </>
  );
}
